import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import * as LucideIcons from 'lucide-react';
import { TreePine, ArrowRight, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThingsToDoPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedActivity, setExpandedActivity] = useState(null);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/activities`);
      setActivities(response.data);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-8 h-8" /> : <LucideIcons.Activity className="w-8 h-8" />;
  };

  const toggleExpand = (activityId) => {
    setExpandedActivity(expandedActivity === activityId ? null : activityId);
  };

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-900/95 border-b border-stone-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <TreePine className="w-8 h-8 text-emerald-400" />
            <span className="text-xl font-semibold text-white">
              Stay <span className="text-emerald-400">Tran-Quil</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-stone-300 hover:text-white transition-colors">Home</Link>
            <Link to="/gallery" className="text-stone-300 hover:text-white transition-colors">Gallery</Link>
            <Link to="/book">
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full px-6">
                Book Now
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <span className="text-emerald-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
              Activities & Adventures
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Things To Do
            </h1>
            <p className="text-stone-300 text-xl max-w-3xl mx-auto">
              From fishing and boating to snowshoeing and stargazing, there's something for everyone at Stay Tran-Quil.
            </p>
          </div>
        </div>
      </section>

      {/* Activities */}
      <main className="max-w-7xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            {activities.map((activity, index) => (
              <div
                key={activity.activity_id}
                className="bg-stone-800/50 border border-stone-700/50 rounded-3xl overflow-hidden"
              >
                <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  {/* Image */}
                  <div className={`relative h-64 md:h-auto ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                    {activity.image_url ? (
                      <img
                        src={activity.image_url}
                        alt={activity.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-stone-700 flex items-center justify-center">
                        <div className="text-emerald-400">
                          {getIcon(activity.icon)}
                        </div>
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="bg-emerald-500/90 text-white text-xs font-medium px-3 py-1 rounded-full">
                        {activity.season}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`p-8 flex flex-col justify-center ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-400">
                        {getIcon(activity.icon)}
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white">
                        {activity.name}
                      </h2>
                    </div>

                    <p className="text-stone-300 text-lg mb-6">
                      {activity.short_description}
                    </p>

                    {/* Expandable Details */}
                    <button
                      onClick={() => toggleExpand(activity.activity_id)}
                      className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                    >
                      {expandedActivity === activity.activity_id ? (
                        <>Hide Details <ChevronUp className="w-5 h-5" /></>
                      ) : (
                        <>Learn More <ChevronDown className="w-5 h-5" /></>
                      )}
                    </button>

                    {expandedActivity === activity.activity_id && (
                      <div className="mt-6 pt-6 border-t border-stone-700">
                        <div 
                          className="prose prose-invert prose-stone max-w-none"
                          dangerouslySetInnerHTML={{ 
                            __html: activity.full_description
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/\n\n/g, '</p><p>')
                              .replace(/\n• /g, '<br/>• ')
                              .replace(/^/, '<p>')
                              .replace(/$/, '</p>')
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Special Feature: The Boat */}
        <div className="mt-16 bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-emerald-700/30 rounded-3xl p-8 md:p-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-400 mx-auto mb-6">
              <LucideIcons.Ship className="w-10 h-10" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">
              Meet "Tran-Quility"
            </h2>
            <p className="text-stone-300 text-lg mb-6">
              Our cabin's boat, Tran-Quility, is a 14-foot aluminum fishing boat with a 9.9HP outboard motor. 
              Perfect for exploring the Winnipeg River, fishing expeditions, or a peaceful sunset cruise. 
              Life jackets and basic fishing gear included with your stay.
            </p>
            <Link to="/book">
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-full">
                Book Your Adventure
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Season Guide */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Seasonal Activity Guide</h2>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { season: 'Spring', months: 'April - May', activities: 'Fishing, Hiking, Wildlife', icon: 'Flower2', color: 'text-green-400' },
              { season: 'Summer', months: 'June - August', activities: 'Swimming, Boating, Campfires', icon: 'Sun', color: 'text-amber-400' },
              { season: 'Fall', months: 'September - November', activities: 'Hunting, Foliage, Fishing', icon: 'Leaf', color: 'text-orange-400' },
              { season: 'Winter', months: 'December - March', activities: 'Ice Fishing, Snowshoeing', icon: 'Snowflake', color: 'text-blue-400' }
            ].map((s) => {
              const Icon = LucideIcons[s.icon];
              return (
                <div key={s.season} className="bg-stone-800/50 border border-stone-700/50 rounded-2xl p-6 text-center">
                  <Icon className={`w-10 h-10 ${s.color} mx-auto mb-3`} />
                  <h3 className="text-xl font-semibold text-white mb-1">{s.season}</h3>
                  <p className="text-stone-500 text-sm mb-3">{s.months}</p>
                  <p className="text-stone-400 text-sm">{s.activities}</p>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer CTA */}
      <section className="bg-stone-950 border-t border-stone-800 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready for Your Adventure?</h2>
          <p className="text-stone-400 mb-8">
            Book your stay and experience everything Stay Tran-Quil has to offer.
          </p>
          <Link to="/book">
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-6 text-lg rounded-full">
              Book Your Stay
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ThingsToDoPage;
